// On a phone the canvas becomes a column: the same widgets, read top to bottom in the order they sit on the board,
// small ones (a number, a clock, a sticky) two to a row. The board's name at the top opens the boards list; the bar
// at the foot has the rest (src/phone/PhoneBar.tsx). Pulling the column down refreshes the board, and a finger held
// on a widget opens its menu. Shared by link, the same column read only, with no bar.

import { lazy, Suspense, useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ChevronDown, RefreshCw } from "lucide-react";
import type { WidgetItem } from "../../shared/types";
import { kindOf } from "../../shared/boardKinds";
import { KINDS } from "../boards/kinds";
import { NotHere } from "../boards/Shell";
import { Recover } from "../chrome/Recover";
import { WidgetTile } from "../widgets/WidgetTile";
import { boardItems, currentBoard, get, useStore } from "../state/store";
import { Mark } from "../chrome/Logo";
import { InstructionsMark, SaveDot } from "../chrome/TopBar";
import { Bell } from "../chrome/Bell";
import { openContextMenu } from "../chrome/ContextMenu";
import { connectSocket, resync } from "../state/sync";
import { runPull } from "../state/sources";
import { BoardMark, PhoneBar } from "../phone/PhoneBar";
import { openSheet } from "../phone/state";
import { useLongPress, usePullToRefresh } from "../phone/gestures";
import { columnOrder, pairHeight, phoneHeight, phoneRows } from "../phone/column";
import { useVoicePrefs } from "../voice/prefs";
import { EmptyKind } from "../onboarding/EmptyKind";

const MicButton = lazy(() => import("../voice/MicButton"));

export { columnOrder };

/** Pulled down: the board as the server has it now, and the widgets on it whose data is older than its schedule read again. */
async function refreshBoard() {
  connectSocket();
  await resync();
  const s = get();
  const due = new Set<string>();
  for (const i of boardItems(s)) {
    if (i.kind !== "widget" || !i.stream) continue;
    const st = s.streams[i.stream];
    const pull = st?.pullId ? s.pulls[st.pullId] : null;
    if (pull && Date.now() - (st.updatedAt || 0) > pull.every * 1000) due.add(pull.id);
  }
  await Promise.allSettled([...due].slice(0, 8).map((id) => runPull(id)));
}

export function MobileBoard({ readOnly = false, itemsOverride, title, shelved = false }: { readOnly?: boolean; itemsOverride?: WidgetItem[]; title?: string; /** the owner's archived board: read only, with the phone's bar and boards list still there to leave it */ shelved?: boolean }) {
  const board = useStore(currentBoard);
  const voiceOn = useVoicePrefs((s) => s.on);
  const own = useStore(useShallow((s) => boardItems(s).filter((i): i is WidgetItem => i.kind === "widget")));
  const items = columnOrder(itemsOverride ?? own);
  // a coworker has no chat: the empty card's asks are the owner's (Features 4)
  const guest = useStore((s) => !!s.guest);
  // a typed board shows its kind's own phone view in the column's place
  const kind = kindOf(board);
  const Phone = kind !== "canvas" ? KINDS[kind]?.Phone : undefined;
  // the elements themselves, so the gestures move with them when a board's switch draws them anew
  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const [mark, setMark] = useState<HTMLDivElement | null>(null);
  const [list, setList] = useState<HTMLDivElement | null>(null);
  usePullToRefresh(scroller, mark, refreshBoard, !readOnly);
  const hold = useCallback((id: string) => {
    const r = document.querySelector(`[data-widget="${id}"]`)?.getBoundingClientRect();
    openContextMenu({ x: r ? r.right - 4 : 0, y: r ? r.top : 0, kind: "item", ids: [id], at: null, align: "right" });
  }, []);
  useLongPress(list, hold, !readOnly);
  const tile = (w: WidgetItem) => (
    <div key={w.id} className={`item widget type-${w.type} mobile-item`} data-widget={w.id} style={{ height: phoneHeight(w) }}>
      <WidgetTile item={w} readOnly={readOnly} />
    </div>
  );
  return (
    <div className={readOnly && !shelved ? "mobile" : "mobile has-bar"} ref={setScroller}>
      {!readOnly && (
        <div ref={setMark} className="phone-pull" aria-hidden>
          <RefreshCw size={16} />
        </div>
      )}
      <header className="mobile-top island">
        {(readOnly && !shelved) || !board ? (
          <>
            <Mark size={18} />
            <span className="mobile-title">{title ?? board?.name}</span>
          </>
        ) : (
          <>
            <button className="mobile-name" onClick={() => openSheet("boards")} aria-label={`${board.name}. Switch board`}>
              <span className="mobile-name-mark">
                <BoardMark board={board} size={17} />
              </span>
              <span className="mobile-name-text">{board.name}</span>
              <ChevronDown size={15} className="mobile-name-chev" />
            </button>
            {/* the board's instructions for agents, on (Features 3): the same quiet mark as on a computer */}
            <InstructionsMark board={board} />
            <SaveDot />
            <Bell />
            {voiceOn && (
              <Suspense fallback={null}>
                <MicButton bare />
              </Suspense>
            )}
          </>
        )}
      </header>
      {kind !== "canvas" && board && (
        <Recover key={board.id}>
          {Phone ? (
            <Suspense fallback={null}>
              <Phone board={board} readOnly={readOnly} />
            </Suspense>
          ) : (
            <NotHere board={board} />
          )}
        </Recover>
      )}
      {/* a canvas's widgets, and those of a kind whose widgets float beside it (a mind map's), under its view */}
      {(kind === "canvas" || (KINDS[kind]?.phoneWidgets && items.length > 0)) && (
        <div ref={setList} className={kind === "canvas" ? "mobile-list" : "mobile-list is-beside"}>
          {phoneRows(items).map((r) =>
            Array.isArray(r) ? (
              <div key={r[0].id + r[1].id} className="mobile-pair" style={{ height: pairHeight(r[0], r[1]) }}>
                {r.map((w) => (
                  <div key={w.id} className={`item widget type-${w.type} mobile-item`} data-widget={w.id}>
                    <WidgetTile item={w} readOnly={readOnly} />
                  </div>
                ))}
              </div>
            ) : (
              tile(r)
            ),
          )}
          {kind === "canvas" && !items.length && !readOnly && !guest && <EmptyKind kind="canvas" compact note={<p className="ob-empty-note">Or add a sticky or a widget from More.</p>} />}
        </div>
      )}
      {(!readOnly || shelved) && <PhoneBar />}
    </div>
  );
}
